import { createFileRoute, useLocation, useNavigate, useSearch } from "@tanstack/react-router";
import { AdministratorProfilesTable } from "./-components/administrator-profiles-table";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useState } from "react";
import { DashboardContext } from "@/components/dashboard-layout";
import { organisationsQueryOptions } from "@/server/organisations";
import { queuesQueryOptions } from "@/server/queues";
import { AdministratorProfileDetails } from "./-components/administrator-profiles-sidebar";
import { AdminProfileReadable, OrganisationReadable, QueueReadable } from "@/lib/mma/types.gen";
import { administratorAccountsQueryOptions, administratorProfilesQueryOptions } from "@/server/administrators";
import { toast } from "sonner";

export const Route = createFileRoute("/_dashboard/administrators/profiles")({
  component: RouteComponent,
  loader: () => {
    return {
      crumb: "Administrator profiles",
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false }) as {
    search?: string;
    organisation?: string;
    queue?: string;
    page?: string;
    perPage?: string;
    sortBy?: string;
    sortDir?: "asc" | "desc";
    id?: string;
  };
  const location = useLocation();
  const context = useContext(DashboardContext);

  const profilesQuery = useQuery(administratorProfilesQueryOptions);
  const profiles = profilesQuery.data as AdminProfileReadable[];
  const organisationsQuery = useQuery(organisationsQueryOptions());
  const queuesQuery = useQuery(queuesQueryOptions);

  // Memoise fallback arrays so they remain referentially stable when there's no data (e.g. on 500 errors)
  const organisations = useMemo(() => organisationsQuery.data ?? [], [organisationsQuery.data]);
  const queues = useMemo(() => queuesQuery.data ?? [], [queuesQuery.data]);
  const accountsQuery = useQuery(administratorAccountsQueryOptions);
  const accounts = accountsQuery.data;

  const modifiedProfiles =
    profiles?.map((profile) => {
      let profileUsers = 0;
      accounts?.forEach((account) => {
        if (account.profileId === profile.id) {
          profileUsers++;
        }
      });
      return {
        ...profile,
        profileUsers,
        accessControlMatrix: profile.accessControlMatrix //Remove this when mock is removed!!!
          ? Object.fromEntries(
              Object.entries(profile.accessControlMatrix).map(([key, value]) => [
                key,
                value as Array<"C" | "R" | "U" | "D">,
              ])
            )
          : {},
      };
    }) || [];

  const isExact = location.href === "/administrators/profiles" || location.href === "/administrators/profiles/";

  if (!context) {
    console.error("DashboardContext is null. Ensure DashboardLayout wraps this component.");
    return <div>Error: DashboardContext is not available.</div>;
  }

  const [isContextReady, setIsContextReady] = useState(false);

  // When the page loads with the sidebar open, the context might not be ready and the sidebar will thus not be set.
  // This effect ensures that the sidebar is set when the context is ready.
  useEffect(() => {
    if (context) {
      setIsContextReady(true);
    }
  }, [context]);

  useEffect(() => {
    if (!isContextReady || !context?.setRightSidebar) {
      return;
    }
    if (isExact && context?.rightSidebar) {
      context.setRightSidebar(null);
    }
    if (organisationsQuery.isLoading || queuesQuery.isLoading) {
      return;
    }
    if (searchParams.id !== undefined && profiles && profiles.length) {
      const profile = profiles.find((profile) => profile.id?.toString() == searchParams.id);
      if (profile) {
        context.setRightSidebar(
          <AdministratorProfileDetails
            profile={profile as AdminProfileReadable}
            organisations={organisations as OrganisationReadable[]}
            queues={queues as QueueReadable[]}
            dataIsLoading={organisationsQuery.isLoading || queuesQuery.isLoading}
          />
        );
      }
      if (searchParams.id === "new") {
        context.setRightSidebar(
          <AdministratorProfileDetails
            profile={null}
            organisations={organisations as OrganisationReadable[]}
            queues={queues as QueueReadable[]}
            dataIsLoading={organisationsQuery.isLoading || queuesQuery.isLoading}
          />
        );
      }
      context.setRightSidebarParamKey?.("id");
    }
  }, [searchParams.id, profiles, isContextReady, organisations, queues]);

  useEffect(() => {
    if (organisationsQuery.isError) {
      const message =
        organisationsQuery.error instanceof Error
          ? organisationsQuery.error.message
          : "Failed to load organisations. Please try again later.";
      toast.error(message);
    }
  }, [organisationsQuery.isError, organisationsQuery.error]);

  useEffect(() => {
    if (profilesQuery.isError) {
      const message =
        profilesQuery.error instanceof Error
          ? profilesQuery.error.message
          : "Failed to load profiles. Please try again later.";
      toast.error(message);
    }
  }, [profilesQuery.isError, profilesQuery.error]);

  useEffect(() => {
    if (queuesQuery.isError) {
      const message =
        queuesQuery.error instanceof Error
          ? queuesQuery.error.message
          : "Failed to load queues. Please try again later.";
      toast.error(message);
    }
  }, [queuesQuery.isError, queuesQuery.error]);

  useEffect(() => {
    if (accountsQuery.isError) {
      const message =
        accountsQuery.error instanceof Error
          ? accountsQuery.error.message
          : "Failed to load accounts. Please try again later.";
      toast.error(message);
    }
  }, [accountsQuery.isError, accountsQuery.error]);

  const handleAddProfile = () => {
    navigate({
      to: "/administrators/profiles",
      replace: true,
      search: (prev) => ({ ...prev, id: "new" }),
    });
  };

  return (
    <AdministratorProfilesTable
      profiles={modifiedProfiles as AdminProfileReadable[]}
      organisations={organisations}
      queues={queues}
      handleAddProfile={handleAddProfile}
      isLoading={organisationsQuery.isLoading || queuesQuery.isLoading}
    />
  );
}
